import { useMemo, useState } from "react";
import { Segmented, Button, Tooltip } from "antd";
import PreviewFrame from "./preview/PreviewFrame";

export default function ExperiencePreview({ config, mode = "builder" }) {
  const [device, setDevice] = useState("desktop");

  const publishMeta = useMemo(() => {
    const slug = config?.seo?.slug || "";
    const status = config?.publish?.status || "draft";
    const publishedAt = config?.publish?.publishedAt || null;
    const publicUrl = slug ? `https://${slug}.netshineone` : "";
    return { slug, status, publishedAt, publicUrl };
  }, [config]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // ignore
    }
  };

  const isMobile = device === "mobile";

  return (
    <div style={styles.shell}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.toolbarTitle}>Live Preview</div>

        <Segmented
          options={[
            { label: "Desktop", value: "desktop" },
            { label: "Mobile", value: "mobile" }
          ]}
          value={device}
          onChange={setDevice}
        />
      </div>

      {/* Publish Preview Card */}
      {mode === "publish" && (
        <div style={styles.publishWrap}>
          <div style={styles.publishCard}>
            <div style={styles.publishTopRow}>
              <div>
                <div style={styles.publishTitle}>Publish Snapshot</div>
                <div style={styles.publishMeta}>
                  Status:{" "}
                  <span style={styles.publishMetaStrong}>
                    {publishMeta.status}
                  </span>
                  {publishMeta.publishedAt ? (
                    <>
                      {" "}
                      · Published:{" "}
                      <span style={styles.publishMetaStrong}>
                        {new Date(publishMeta.publishedAt).toLocaleString()}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>

              <div style={styles.publishActions}>
                <Tooltip
                  title={
                    !publishMeta.publicUrl
                      ? "Set SEO slug to generate URL"
                      : "Open public page"
                  }
                >
                  <Button
                    size="small"
                    disabled={!publishMeta.publicUrl}
                    onClick={() =>
                      publishMeta.publicUrl &&
                      window.open(publishMeta.publicUrl, "_blank")
                    }
                  >
                    Open Public URL
                  </Button>
                </Tooltip>

                <Tooltip title="Copy link">
                  <Button
                    size="small"
                    disabled={!publishMeta.publicUrl}
                    onClick={() =>
                      publishMeta.publicUrl && copy(publishMeta.publicUrl)
                    }
                  >
                    Copy
                  </Button>
                </Tooltip>
              </div>
            </div>

            <div
              style={styles.publishUrlBar}
              title={publishMeta.publicUrl || "Set slug in SEO & Domain"}
            >
              {publishMeta.publicUrl ||
                "Set slug in SEO & Domain to generate your public URL"}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Premium Preview Background + Scroll */}
      <div style={styles.previewArea}>
        {/* Soft aurora blobs */}
        <div style={styles.auroraA} />
        <div style={styles.auroraB} />
        <div style={styles.auroraC} />

        {/* Scroll container */}
        <div style={styles.previewScroll}>
          {/* Frame */}
          <div style={isMobile ? styles.mobileFrame : styles.desktopFrame}>
            {/* Inner surface */}
            <div style={styles.frameInner}>
              <PreviewFrame config={config} device={device} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------
   Styles (inline, no external CSS needed)
------------------------------------------- */
const styles = {
  shell: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: "#0b0d12"
  },

  toolbar: {
    padding: "12px 14px",
    background: "#0f1115",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10
  },
  toolbarTitle: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: 0.2
  },

  publishWrap: {
    padding: 12,
    background:
      "linear-gradient(180deg, rgba(15,17,21,0.95), rgba(15,17,21,0.55))",
    borderBottom: "1px solid rgba(255,255,255,0.10)"
  },
  publishCard: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    backdropFilter: "blur(8px)"
  },
  publishTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap"
  },
  publishTitle: { color: "#fff", fontWeight: 950, fontSize: 14 },
  publishMeta: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 12,
    marginTop: 4
  },
  publishMetaStrong: { color: "#fff", fontWeight: 900 },
  publishActions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap"
  },
  publishUrlBar: {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.75)",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  // Preview area
  previewArea: {
    position: "relative",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    background:
      "radial-gradient(1200px 700px at 20% 0%, rgba(157,240,61,0.10), transparent 55%), radial-gradient(900px 600px at 90% 30%, rgba(96,165,250,0.12), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  },

  // aurora blobs
  auroraA: {
    position: "absolute",
    inset: "auto auto 20% -10%",
    width: 420,
    height: 420,
    borderRadius: 999,
    filter: "blur(40px)",
    background: "rgba(157,240,61,0.18)",
    pointerEvents: "none"
  },
  auroraB: {
    position: "absolute",
    inset: "10% -10% auto auto",
    width: 460,
    height: 460,
    borderRadius: 999,
    filter: "blur(44px)",
    background: "rgba(96,165,250,0.16)",
    pointerEvents: "none"
  },
  auroraC: {
    position: "absolute",
    inset: "auto 15% -18% auto",
    width: 520,
    height: 520,
    borderRadius: 999,
    filter: "blur(52px)",
    background: "rgba(244,114,182,0.10)",
    pointerEvents: "none"
  },

  previewScroll: {
    position: "relative",
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
    padding: 16,
    scrollBehavior: "smooth"
  },

  // Frames
  desktopFrame: {
    maxWidth: 1320,
    margin: "0 auto",
    borderRadius: 22,
    border: "1px solid rgba(255,255,255,0.10)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
    boxShadow:
      "0 30px 90px rgba(0,0,0,0.45), 0 10px 30px rgba(0,0,0,0.35)",
    overflow: "hidden"
  },
  mobileFrame: {
    width: 420,
    maxWidth: "100%",
    margin: "0 auto",
    borderRadius: 34,
    border: "1px solid rgba(255,255,255,0.14)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
    boxShadow:
      "0 40px 120px rgba(0,0,0,0.55), 0 10px 35px rgba(0,0,0,0.35)",
    overflow: "hidden"
  },

  frameInner: {
    background: "#05070a"
  }
};
